import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/logo.png';
import fullscreenIcon from '../assets/fullscreen-icon.svg';
import progressRunnerIcon from '../assets/progress-runner.svg';
import questionNavArrow from '../assets/question-nav-arrow.svg';
 
const NAV_WIDTH        = 455;
const MAX_TAB_SWITCHES = 2;
 
const isDocumentFullscreen = () => !!(
  document.fullscreenElement       ||
  document.webkitFullscreenElement ||
  document.mozFullScreenElement    ||
  document.msFullscreenElement
);
 
export default function ExamPage() {
  const EXAM_ID          = parseInt(localStorage.getItem('activeExamId') || '1', 10);
  const EXAM_TITLE       = localStorage.getItem('activeExamTitle') || 'Assessment';
  const storedUser       = JSON.parse(localStorage.getItem('user') || '{}');
  const selectedSectionsKey = `selectedOptionalSections:${storedUser.email || storedUser.id || 'guest'}:${EXAM_ID}`;
  const scheduledCloseAtRaw = localStorage.getItem(`examClosesAt_${EXAM_ID}`);
  const scheduledCloseAtMs = scheduledCloseAtRaw ? Date.parse(scheduledCloseAtRaw) : NaN;
  
  // Dynamic duration calculation: default to 60 minutes if not present
  const examDurationMin  = parseInt(localStorage.getItem(`examDuration_${EXAM_ID}`) || '60', 10);
  const totalDurationSeconds = examDurationMin * 60;

  const [questions, setQuestions]   = useState([]);
  const [totalQuestionsCount, setTotalQuestionsCount] = useState(0);
  const [current, setCurrent]       = useState(0);
  const [answers, setAnswers]       = useState({});
  const [visited, setVisited]       = useState(new Set());
  const [skipped, setSkipped]       = useState(new Set());
  const [timeLeft, setTimeLeft]     = useState(() => {
    if (Number.isFinite(scheduledCloseAtMs)) {
      localStorage.setItem(`examEndTime_${EXAM_ID}`, String(scheduledCloseAtMs));
      return Math.max(0, Math.floor((scheduledCloseAtMs - Date.now()) / 1000));
    }
    const savedEnd = localStorage.getItem(`examEndTime_${EXAM_ID}`);
    if (savedEnd) {
      const remaining = Math.round((parseInt(savedEnd, 10) - Date.now()) / 1000);
      if (remaining > 0) return remaining;
    }
    const endTime = Date.now() + totalDurationSeconds * 1000;
    localStorage.setItem(`examEndTime_${EXAM_ID}`, endTime);
    return totalDurationSeconds;
  });
  const [navOpen, setNavOpen]       = useState(true);
  const [showHelp, setShowHelp]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showAssessmentEnded, setShowAssessmentEnded] = useState(false);
 
  // ── NEW: active section index for the navigator ──
  const [activeSection, setActiveSection] = useState(-1);
  // ── NEW: pagination page for question grid ──
  const [navPage, setNavPage] = useState(1);
 
  // ── Anti-cheat state ──
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [tabWarningMsg, setTabWarningMsg]   = useState('');
  const [showFsPrompt, setShowFsPrompt]     = useState(true);
  const [showFsWarning, setShowFsWarning]   = useState(false);
  const [screenShareError, setScreenShareError] = useState('');
 
  const tabSwitchRef  = useRef(0);
  const screenshotVideoRef = useRef(null);
  const submittingRef = useRef(false);
  const switchLockRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingMimeTypeRef = useRef('video/webm');
  const recordingStopPromiseRef = useRef(null);
  const recordingStopResolveRef = useRef(null);
  const pendingRecordingBlobRef = useRef(null);
  const handleSubmitRef = useRef(null);
  const examMonitoringActiveRef = useRef(false);
  const ignoreSwitchesUntilRef = useRef(0);
  const showFsPromptRef = useRef(showFsPrompt);
  const suppressFullscreenWarningRef = useRef(false);
 
  const navigate = useNavigate();
 
  // EXAM_ID is fixed for the lifetime of the mounted exam session.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    localStorage.removeItem(`tabSwitch_${EXAM_ID}`);
    tabSwitchRef.current = 0;
  }, [EXAM_ID]);
 
  useEffect(() => {
    showFsPromptRef.current = showFsPrompt;
  }, [showFsPrompt]);
 
  // ── DYNAMIC QUESTIONS FROM DATABASE ────────────────────────
  // The local storage key is derived from the active exam and candidate identity on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/exam/${EXAM_ID}/questions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.length > 0) {
          let selectedOptional = [];
          try {
            selectedOptional = JSON.parse(localStorage.getItem(selectedSectionsKey) || '[]');
          } catch (e) {
            console.warn('Could not parse selected optional sections from localStorage', e);
          }
          const selectedSet = new Set(selectedOptional.map(s => s.trim().toLowerCase()));

          const mapped = res.data.map(q => ({
            id: q.id,
            sectionId: q.section_id,
            section: q.section || 'General',
            text: q.question_text,
            options: [q.option_a, q.option_b, q.option_c, q.option_d],
            marks: q.marks || 1,
            isOptional: q.is_optional || false
          }));

          const filtered = mapped.filter(q => {
            if (!q.isOptional) return true;
            return selectedSet.has(String(q.sectionId)) || selectedSet.has(q.section.trim().toLowerCase());
          });

          setQuestions(filtered);
          setTotalQuestionsCount(filtered.length);
        } else {
          throw new Error('No questions found in database');
        }
      } catch (err) {
        console.warn('Could not load assigned exam questions:', err);
        setQuestions([]);
        setTotalQuestionsCount(0);
        /*
        const mockQuestions = [
          { id: 1,  section: 'Aptitude',         text: 'What is 15% of 200?',                                                                      options: ['25', '30', '35', '40'] },
          { id: 2,  section: 'Aptitude',         text: 'If a train travels 60 km/h for 2.5 hours, how far does it go?',                            options: ['120 km', '150 km', '180 km', '200 km'] },
          { id: 3,  section: 'Aptitude',         text: 'Which number comes next: 2, 6, 12, 20, __?',                                               options: ['28', '30', '32', '36'] },
          { id: 4,  section: 'Logical Reasoning',text: 'A is taller than B. C is shorter than A. Who is the tallest?',                             options: ['A', 'B', 'C', 'Cannot determine'] },
          { id: 5,  section: 'Logical Reasoning',text: 'What is the square root of 144?',                                                          options: ['10', '11', '12', '13'] },
          { id: 6,  section: 'Logical Reasoning',text: 'If all Bloops are Razzies and all Razzies are Lazzies, are all Bloops definitely Lazzies?', options: ['Yes', 'No', 'Maybe', 'Cannot say'] },
          { id: 7,  section: 'Python',           text: 'A shopkeeper buys an item for ₹400 and sells it for ₹500. What is the profit percentage?',  options: ['20%', '25%', '15%', '10%'] },
          { id: 8,  section: 'Python',           text: 'Which word is the odd one out: Apple, Mango, Carrot, Banana?',                             options: ['Apple', 'Mango', 'Carrot', 'Banana'] },
          { id: 9,  section: 'DevOps',           text: 'How many months have 28 days?',                                                            options: ['1', '2', '6', '12'] },
          { id: 10, section: 'DevOps',           text: 'If 5 workers complete a job in 8 days, how many days will 10 workers take?',               options: ['2', '3', '4', '6'] },
        ];
        setQuestions(mockQuestions);
        setTotalQuestionsCount(mockQuestions.length);
        */
      }
    };
    fetchQuestions();
  }, [EXAM_ID, selectedSectionsKey]);
 
  // ── Derived section list from questions (memoized to prevent infinite loops) ──
  const sections = useMemo(() => {
    return questions.length
      ? [...new Map(questions.map(q => [q.section, q.section])).keys()]
      : [];
  }, [questions]);
 
  // Questions for the active section (for the grid)
  const sectionQuestions = activeSection === -1
    ? questions
    : questions.filter(q => q.section === sections[activeSection]);
 
  // Questions per page in the grid
  const GRID_PAGE_SIZE = 10;
  const totalNavPages  = Math.max(1, Math.ceil(sectionQuestions.length / GRID_PAGE_SIZE));
  const pagedQuestions = sectionQuestions.slice((navPage - 1) * GRID_PAGE_SIZE, navPage * GRID_PAGE_SIZE);
 
  const switchSection = useCallback((sectionIndex) => {
    setActiveSection(sectionIndex);
    setNavPage(1);
    if (sectionIndex === -1) {
      setCurrent(0);
      return;
    }
    const targetSectionName = sections[sectionIndex];
    if (targetSectionName) {
      const qIdx = questions.findIndex(qq => qq.section === targetSectionName);
      if (qIdx !== -1) {
        setCurrent(qIdx);
      }
    }
  }, [sections, questions]);
 
  // ── Auto-sync activeSection and navPage with current viewed question index ──
  useEffect(() => {
    if (questions.length === 0 || !questions[current]) return;
    if (activeSection === -1) {
      setNavPage(Math.floor(current / GRID_PAGE_SIZE) + 1);
      return;
    }
    const currentQ = questions[current];
    const currentSectionName = currentQ.section;
    
    const secIdx = sections.indexOf(currentSectionName);
    if (secIdx !== -1) {
      setActiveSection(secIdx);
      
      const sectionQuestionsList = questions.filter(qq => qq.section === currentSectionName);
      const idxInSection = sectionQuestionsList.findIndex(qq => qq.id === currentQ.id);
      if (idxInSection !== -1) {
        const targetPage = Math.floor(idxInSection / GRID_PAGE_SIZE) + 1;
        setNavPage(targetPage);
      }
    }
  }, [activeSection, current, questions, sections]);
 
  // EXAM_ID stays stable throughout one exam attempt.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const uploadRecording = useCallback(async (blob, reason = 'completed') => {
    if (!blob || blob.size === 0) { console.warn('Skipping recording upload because blob is empty'); return null; }
    const token = localStorage.getItem('token');
    if (!token) { console.error('No authentication token found for recording upload'); return null; }
    const extension = blob.type.includes('mp4') ? 'mp4' : 'webm';
    const formData = new FormData();
    formData.append('recording', blob, `exam-${EXAM_ID}-${Date.now()}.${extension}`);
    formData.append('examId', String(EXAM_ID));
    formData.append('stopReason', reason);
    try {
      const response = await axios.post('/api/exam/upload-recording', formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      return response.data;
    } catch (error) { console.error('Error uploading recording:', error.response?.data || error.message); return null; }
  }, [EXAM_ID]);
 
  const createScreenshotBlob = useCallback(async () => {
    const stream = screenshotVideoRef.current?.srcObject;
    const videoTrack = stream?.getVideoTracks?.()[0];
    if (videoTrack && typeof ImageCapture !== 'undefined') {
      try {
        const imageCapture = new ImageCapture(videoTrack);
        const bitmap = await imageCapture.grabFrame();
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width; canvas.height = bitmap.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height);
        if (typeof bitmap.close === 'function') bitmap.close();
        return await new Promise((resolve) => { canvas.toBlob((blob) => resolve(blob), 'image/png'); });
      } catch (error) { console.warn('Track-based screenshot capture failed:', error); }
    }
    const video = screenshotVideoRef.current;
    if (!video || video.readyState < 2) return null;
    const width = video.videoWidth || 1280; const height = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, width, height);
    return await new Promise((resolve) => { canvas.toBlob((blob) => resolve(blob), 'image/png'); });
  }, []);
 
  const stopStreamTracks = useCallback((stream) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => { try { track.stop(); } catch (e) {} });
  }, []);
 
  const cleanupMonitoringStream = useCallback(() => {
    try {
      const stream = screenshotVideoRef.current?.srcObject;
      if (stream) { stream.getTracks().forEach((track) => { try { track.stop(); } catch (e) {} }); if (typeof stream.close === 'function') stream.close(); }
      if (screenshotVideoRef.current) { screenshotVideoRef.current.srcObject = null; if (screenshotVideoRef.current.parentNode) screenshotVideoRef.current.parentNode.removeChild(screenshotVideoRef.current); }
    } catch (e) { console.error('Error during monitoring cleanup:', e); }
    finally {
      screenshotVideoRef.current = null; mediaRecorderRef.current = null; recordingChunksRef.current = [];
      recordingStopPromiseRef.current = null; recordingStopResolveRef.current = null; pendingRecordingBlobRef.current = null;
      examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0;
    }
  }, []);
 
  const stopExamRecording = useCallback(async (reason = 'completed') => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      if (!recordingStopPromiseRef.current) { recordingStopPromiseRef.current = new Promise((resolve) => { recordingStopResolveRef.current = resolve; }); }
      recorder.stop();
      try { const blob = await recordingStopPromiseRef.current; if (blob) { await uploadRecording(blob, reason); pendingRecordingBlobRef.current = null; } }
      finally { cleanupMonitoringStream(); }
      return;
    }
    if (pendingRecordingBlobRef.current) { await uploadRecording(pendingRecordingBlobRef.current, reason); pendingRecordingBlobRef.current = null; }
    cleanupMonitoringStream();
  }, [cleanupMonitoringStream, uploadRecording]);
 
  const startScreenRecording = useCallback((stream) => {
    if (typeof MediaRecorder === 'undefined') return;
    const mimeTypeCandidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    const mimeType = mimeTypeCandidates.find((c) => (typeof MediaRecorder.isTypeSupported === 'function' ? MediaRecorder.isTypeSupported(c) : c === 'video/webm')) || '';
    recordingMimeTypeRef.current = mimeType || 'video/webm';
    recordingChunksRef.current = []; recordingStopPromiseRef.current = null; recordingStopResolveRef.current = null;
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(recordingChunksRef.current, { type: recordingMimeTypeRef.current || 'video/webm' });
      pendingRecordingBlobRef.current = blob;
      const resolve = recordingStopResolveRef.current;
      recordingStopResolveRef.current = null; recordingStopPromiseRef.current = null;
      if (resolve) resolve(blob);
    };
    recorder.onerror = (e) => { console.error('MediaRecorder error:', e); const resolve = recordingStopResolveRef.current; recordingStopResolveRef.current = null; recordingStopPromiseRef.current = null; if (resolve) resolve(null); };
    recorder.start(1000); mediaRecorderRef.current = recorder;
  }, []);
 
  // EXAM_ID stays stable throughout one exam attempt.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const captureScreenshot = useCallback(async (eventType) => {
    const blob = await createScreenshotBlob();
    if (!blob) return;
    const formData = new FormData();
    formData.append('screenshot', blob, `screenshot-${eventType}-${Date.now()}.png`);
    formData.append('eventType', eventType); formData.append('examId', String(EXAM_ID));
    const token = localStorage.getItem('token');
    if (!token) return;
    try { await axios.post('/api/exam/upload-screenshot', formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }); }
    catch (e) { console.error('Error uploading screenshot:', e.response?.data || e.message); }
  }, [createScreenshotBlob, EXAM_ID]);
 
  // EXAM_ID stays stable throughout one exam attempt.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmit = useCallback(async (reason = 'manual', options = {}) => {
    const { shouldNavigate = true } = options;
    if (submittingRef.current) return;
    submittingRef.current = true; setSubmitting(true);
    try {
      const answeredCount = Object.keys(answers).length;
      const elapsedSeconds = Math.max(0, totalDurationSeconds - timeLeft);
      const formattedAnswers = {};
      Object.entries(answers).forEach(([qId, optIdx]) => { formattedAnswers[qId] = ['a', 'b', 'c', 'd'][optIdx]; });
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `/api/exam/${EXAM_ID}/submit`,
        { answers: formattedAnswers, submitReason: reason, timeTakenSeconds: elapsedSeconds, tabSwitchCount: tabSwitchRef.current },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const resultSummary = { ...res.data, totalQuestions: res.data.totalQuestions ?? totalQuestionsCount, answeredCount: res.data.answeredCount ?? answeredCount, timeTaken: res.data.timeTaken ?? elapsedSeconds, submittedAt: res.data.submittedAt || new Date().toISOString(), submitReason: reason };
      localStorage.setItem('result', JSON.stringify(resultSummary));
      sessionStorage.setItem('result', JSON.stringify(resultSummary));
    } catch {
      const answeredCount = Object.keys(answers).length;
      const elapsedSeconds = Math.max(0, totalDurationSeconds - timeLeft);
      const fallbackSummary = { score: answeredCount, percentage: totalQuestionsCount > 0 ? Math.round((answeredCount / totalQuestionsCount) * 100) : 0, isPassed: totalQuestionsCount > 0 ? (answeredCount / totalQuestionsCount) * 100 >= 60 : false, totalQuestions: totalQuestionsCount, answeredCount, timeTaken: elapsedSeconds, submittedAt: new Date().toISOString(), submitReason: reason };
      localStorage.setItem('result', JSON.stringify(fallbackSummary));
      sessionStorage.setItem('result', JSON.stringify(fallbackSummary));
    } finally {
      localStorage.removeItem(`examEndTime_${EXAM_ID}`);
      await stopExamRecording(reason);
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (e) {}
      if (!shouldNavigate) return;
      navigate('/result');
    }
  }, [answers, timeLeft, totalQuestionsCount, navigate, stopExamRecording, totalDurationSeconds, EXAM_ID]);
 
  useEffect(() => { handleSubmitRef.current = handleSubmit; }, [handleSubmit]);
 
  useEffect(() => {
    const resolveEndTimeMs = () => {
      if (Number.isFinite(scheduledCloseAtMs)) return scheduledCloseAtMs;
      const savedEnd = parseInt(localStorage.getItem(`examEndTime_${EXAM_ID}`) || '', 10);
      return Number.isFinite(savedEnd) ? savedEnd : NaN;
    };

    const tick = () => {
      const endTimeMs = resolveEndTimeMs();
      if (!Number.isFinite(endTimeMs)) {
        setTimeLeft(0);
        return;
      }

      const remaining = Math.max(0, Math.floor((endTimeMs - Date.now()) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 0) {
        handleSubmit('timeout');
      }
    };

    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [handleSubmit, EXAM_ID, scheduledCloseAtMs]);
 
  useEffect(() => { return () => { cleanupMonitoringStream(); }; }, [cleanupMonitoringStream]);
 
  useEffect(() => {
    const h = () => { cleanupMonitoringStream(); };
    window.addEventListener('beforeunload', h);
    return () => { window.removeEventListener('beforeunload', h); };
  }, [cleanupMonitoringStream]);
 
  const recordSwitch = useCallback((source) => {
    if (switchLockRef.current || submittingRef.current || !examMonitoringActiveRef.current || Date.now() < ignoreSwitchesUntilRef.current) return;
    switchLockRef.current = true; setTimeout(() => { switchLockRef.current = false; }, 500);
    tabSwitchRef.current += 1;
    const count = tabSwitchRef.current; localStorage.setItem(`tabSwitch_${EXAM_ID}`, count);
    const remaining = MAX_TAB_SWITCHES - count;
    const takeScreenshot = async () => {
      let blob = await createScreenshotBlob();
      let eventType = 'tab_switch';
      if (!blob) {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ff0000';
          ctx.fillRect(0, 0, 1, 1);
        }
        blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        eventType = 'tab_switch_no_stream';
      }
      if (!blob) return;
      const formData = new FormData();
      formData.append('screenshot', blob, `screenshot-${eventType}-${Date.now()}.png`);
      formData.append('eventType', eventType);
      formData.append('examId', String(EXAM_ID));
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        await axios.post('/api/exam/upload-screenshot', formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      } catch (e) {
        console.error('Error uploading tab switch screenshot:', e.response?.data || e.message);
      }
    };
    if (count >= MAX_TAB_SWITCHES) {
      setShowTabWarning(false); setShowAssessmentEnded(true);
      (async () => { await takeScreenshot(); await handleSubmit('tab_switch', { shouldNavigate: false }); })();
    } else {
      void takeScreenshot();
      setTabWarningMsg(`You switched tabs ${count} time${count > 1 ? 's' : ''}. ${remaining} chance${remaining > 1 ? 's are' : ' is'} left before the test is ended automatically.`);
      setShowTabWarning(true);
    }
  }, [createScreenshotBlob, handleSubmit, EXAM_ID]);
 
  useEffect(() => {
    const h = () => { if (document.hidden) recordSwitch('tab_switch'); };
    document.addEventListener('visibilitychange', h);
    return () => document.removeEventListener('visibilitychange', h);
  }, [recordSwitch]);
 
  const enterFullscreen = async () => {
    const el = document.documentElement;
    suppressFullscreenWarningRef.current = true; setScreenShareError('');
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
      else if (el.msRequestFullscreen) await el.msRequestFullscreen();
    } catch (e) {
      setScreenShareError('Fullscreen is required to start the exam. Please allow fullscreen mode and try again.');
      setShowFsPrompt(true); examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0; return;
    }
    if (!isDocumentFullscreen()) {
      setScreenShareError('Fullscreen is required to start the exam. Please allow fullscreen mode and try again.');
      setShowFsPrompt(true); examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0; return;
    }
    setShowFsPrompt(false); setShowFsWarning(false);
    examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = Date.now() + 4000;
    if (!window.isSecureContext) {
      setScreenShareError('Screen sharing requires HTTPS with a valid SSL certificate.');
      setShowFsPrompt(true); examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0; return;
    }
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setScreenShareError('Screen sharing is not available in this browser.');
      setShowFsPrompt(true); examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0; return;
    }
    if (!screenshotVideoRef.current) {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: { mediaSource: 'screen' } });
        const [videoTrack] = stream.getVideoTracks();
        if (videoTrack) {
          const displaySurface = videoTrack.getSettings?.().displaySurface;
          if (displaySurface !== 'monitor') {
            stopStreamTracks(stream);
            setScreenShareError('Entire Screen sharing is required. Please choose your full screen in the share dialog.');
            setShowFsPrompt(true); setShowFsWarning(false); examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0; return;
          }
          videoTrack.addEventListener('ended', () => {
            if (submittingRef.current) return;
            if (screenshotVideoRef.current) screenshotVideoRef.current.srcObject = null;
            screenshotVideoRef.current = null;
            examMonitoringActiveRef.current = false;
            setScreenShareError('Screen sharing stopped. Please share your entire screen again to continue the test.');
            setShowFsPrompt(true);
            setShowFsWarning(false);
          }, { once: true });
        }
        const screenshotVideo = document.createElement('video');
        screenshotVideo.style.display = 'none'; screenshotVideo.autoplay = true; screenshotVideo.muted = true;
        screenshotVideo.playsInline = true; screenshotVideo.srcObject = stream; screenshotVideoRef.current = screenshotVideo;
        await new Promise((resolve) => {
          if (screenshotVideo.readyState >= 2) resolve();
          else { screenshotVideo.addEventListener('loadeddata', resolve, { once: true }); screenshotVideo.addEventListener('error', () => resolve(), { once: true }); }
        });
        await screenshotVideo.play();
        startScreenRecording(stream);
        if (!isDocumentFullscreen()) {
          setScreenShareError('Screen sharing is active. Please click Enable Fullscreen again to continue.');
          setShowFsPrompt(true); setShowFsWarning(false); examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0; return;
        }
        examMonitoringActiveRef.current = true; ignoreSwitchesUntilRef.current = Date.now() + 4000;
        suppressFullscreenWarningRef.current = false;
      } catch (error) {
        const message = error?.name === 'NotAllowedError'
          ? 'Screen sharing permission is required. Please click Enable Fullscreen again and allow sharing your entire screen.'
          : 'Screen sharing could not be started. Please allow entire-screen sharing and try again.';
        setScreenShareError(message);
        setShowFsPrompt(true); setShowFsWarning(false); examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0;
      }
    } else {
      if (!isDocumentFullscreen()) {
        setScreenShareError('Fullscreen is required to continue the exam.');
        setShowFsPrompt(true); setShowFsWarning(false); examMonitoringActiveRef.current = false; ignoreSwitchesUntilRef.current = 0; return;
      }
      examMonitoringActiveRef.current = true; ignoreSwitchesUntilRef.current = Date.now() + 2000;
      suppressFullscreenWarningRef.current = false;
    }
  };
 
  useEffect(() => {
    const handler = () => {
      const inFs = isDocumentFullscreen();
      if (!inFs && !submittingRef.current && !showFsPromptRef.current && !suppressFullscreenWarningRef.current) {
        //examMonitoringActiveRef.current = false;
        setScreenShareError('Fullscreen is required to continue the exam.');
        setShowFsPrompt(true);
        setShowFsWarning(false);
      }
    };
    document.addEventListener('fullscreenchange', handler); document.addEventListener('webkitfullscreenchange', handler);
    document.addEventListener('mozfullscreenchange', handler); document.addEventListener('MSFullscreenChange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler); document.removeEventListener('webkitfullscreenchange', handler);
      document.removeEventListener('mozfullscreenchange', handler); document.removeEventListener('MSFullscreenChange', handler);
    };
  }, []);
 
  useEffect(() => {
    const handler = e => {
      if (e.key === 'F11') { e.preventDefault(); return false; }
      if ((e.ctrlKey && ['c','C','v','V','a','A','u','U','s','S','p','P'].includes(e.key)) || (e.ctrlKey && e.shiftKey && ['I','i','J','j','C','c'].includes(e.key)) || e.key === 'F12' || (e.metaKey && ['c','C'].includes(e.key))) { e.preventDefault(); e.stopPropagation(); return false; }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, []);
 
  useEffect(() => {
    const noop = e => e.preventDefault();
    document.addEventListener('contextmenu', noop); document.addEventListener('copy', noop);
    document.addEventListener('cut', noop); document.addEventListener('paste', noop); document.addEventListener('selectstart', noop);
    return () => {
      document.removeEventListener('contextmenu', noop); document.removeEventListener('copy', noop);
      document.removeEventListener('cut', noop); document.removeEventListener('paste', noop); document.removeEventListener('selectstart', noop);
    };
  }, []);

  const q = questions[current];

  useEffect(() => {
    if (!q?.id) return;
    setVisited(prev => {
      if (prev.has(q.id)) return prev;
      return new Set([...prev, q.id]);
    });
  }, [q?.id]);
 
  const mins     = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const secs     = String(timeLeft % 60).padStart(2, '0');
 
  if (questions.length === 0) return <div style={s.page}>Loading questions...</div>;
  if (!q) return <div style={s.page}>Error: Question not found at current index.</div>;
 
  const effectiveSkipped = new Set([
    ...[...skipped].filter(id => answers[id] === undefined && id !== q.id),
    ...questions
      .filter(qq => visited.has(qq.id) && answers[qq.id] === undefined && qq.id !== q.id)
      .map(qq => qq.id),
  ]);
  const answered = Object.keys(answers).length;
  const skippedCount = effectiveSkipped.size;
  const currentVisitedUnanswered = visited.has(q.id) && answers[q.id] === undefined;
  const notVisited = Math.max(0, totalQuestionsCount - answered - skippedCount - (currentVisitedUnanswered ? 1 : 0));
  const progress     = totalQuestionsCount > 1 ? (current / (totalQuestionsCount - 1)) * 100 : 0;
 
  const selectOption = idx => {
    setVisited(prev => new Set([...prev, q.id]));
    setSkipped(prev => {
      const next = new Set(prev);
      next.delete(q.id);
      return next;
    });
    setAnswers(p => ({ ...p, [q.id]: idx }));
  };
  const handleSkip   = () => {
    setVisited(prev => new Set([...prev, q.id]));
    setSkipped(p => new Set([...p, q.id]));
    if (current < questions.length - 1) setCurrent(c => c + 1);
  };
  const handleNext   = () => { if (current < questions.length - 1) setCurrent(c => c + 1); };
  const handlePrev   = () => { if (current > 0) setCurrent(c => c - 1); };
 
  const getStatus = id => {
    if (id === q.id)               return 'current';
    if (answers[id] !== undefined) return 'answered';
    if (effectiveSkipped.has(id))  return 'skipped';
    return 'unvisited';
  };
 
  // Section question counts for pills
  const sectionCounts = [
    { name: 'All', count: questions.length, sectionIndex: -1 },
    ...sections.map((sec, idx) => ({
      name: sec,
      count: questions.filter(qq => qq.section === sec).length,
      sectionIndex: idx,
    })),
  ];
 
  return (
    <div style={{ ...s.page, userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' }}>
 
      {/* ── Fullscreen entry prompt ── */}
      {showFsPrompt && (
        <div style={s.overlay}>
          <div style={{ position: 'relative', width: 548, height: 382, maxWidth: '92%', background: '#FFFFFF', borderRadius: 24, boxShadow: '0px 8px 32px rgba(0,0,0,0.12)', textAlign: 'center', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 31 }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#EBF3FE', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <img src={fullscreenIcon} alt="fullscreen" style={{ width: 36, height: 36 }} />
            </div>
            <h2 style={{ width: 392, maxWidth: '100%', fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: '#1F2937', margin: '0 0 18px', lineHeight: '32px' }}>Switch to Full screen to Continue</h2>
            <p style={{ width: 441, maxWidth: '100%', minHeight: 48, fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 14, color: '#7C8594', lineHeight: '24px', margin: '24px 0 40px', textAlign: 'center', whiteSpace: 'pre-line' }}>{'Please switch to fullscreen mode before starting the test.\nDo not exit fullscreen during the assessment.'}</p>
            {screenShareError && (<p style={{ color: '#DC2626', fontSize: 13, margin: '-20px 0 16px', padding: '0 24px', lineHeight: '20px' }}>{screenShareError}</p>)}
            <button onClick={enterFullscreen} style={{ width: 182, height: 48, background: '#4D8BF1', color: '#fff', border: 'none', borderRadius: 999, fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14, cursor: 'pointer', lineHeight: '21px', boxShadow: '0 6px 14px rgba(77,139,241,0.18)' }}>Enable Fullscreen</button>
          </div>
        </div>
      )}
 
      {/* ── Fullscreen exit warning ── */}
      {showFsWarning && (
        <div style={s.overlay}>
          <div style={{ position: 'relative', width: 480, background: '#FFFFFF', borderRadius: 24, boxShadow: '0px 8px 32px rgba(0,0,0,0.12)', padding: '40px 40px 40px', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FFF8E6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="#F5A623" stroke="#F5A623" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/><line x1="12" y1="9" x2="12" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round"/><line x1="12" y1="17" x2="12.01" y2="17" stroke="white" strokeWidth="2.5" strokeLinecap="round"/></svg>
            </div>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: '#1A1D1F', margin: '0 0 16px', lineHeight: '30px' }}>Fullscreen Exited</h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 16, color: '#6F7478', lineHeight: '24px', margin: '0 0 32px', whiteSpace: 'pre-line' }}>{'To continue the test, please return to fullscreen mode.\nExiting fullscreen may end your test.'}</p>
            <button onClick={() => { enterFullscreen(); setShowFsWarning(false); }} style={{ background: '#4D8BF1', color: '#fff', border: 'none', borderRadius: 52, padding: '13px 27px', fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Enable Fullscreen</button>
          </div>
        </div>
      )}
 
      {/* ── Tab switch warning ── */}
      {showTabWarning && (
        <div style={s.overlay}>
          <div style={{ position: 'relative', width: 480, background: '#FFFFFF', borderRadius: 24, boxShadow: '0px 8px 32px rgba(0,0,0,0.12)', padding: '40px 40px 40px', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FFF8E6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="#F5A623" stroke="#F5A623" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/><line x1="12" y1="9" x2="12" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round"/><line x1="12" y1="17" x2="12.01" y2="17" stroke="white" strokeWidth="2.5" strokeLinecap="round"/></svg>
            </div>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: '#1A1D1F', margin: '0 0 16px' }}>Tab Switch Detected</h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#374151', margin: '0 0 20px', lineHeight: '24px' }}>{tabWarningMsg}</p>
            <div style={{ background: '#EEF4FF', border: '1px solid #DBEAFE', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, textAlign: 'left' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4D8BF1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1E40AF' }}>Please return to the assessment immediately and avoid switching tabs again.</span>
            </div>
            <button onClick={() => { enterFullscreen(); setShowFsWarning(false); setShowTabWarning(false); }} style={{ background: '#4D8BF1', color: '#fff', border: 'none', borderRadius: 52, padding: '13px 40px', fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Back to Exam</button>
          </div>
        </div>
      )}
 
      {/* ── Assessment Ended modal ── */}
      {showAssessmentEnded && (
        <div style={s.overlay}>
          <div style={{ position: 'relative', width: 480, background: '#FFFFFF', borderRadius: 24, boxShadow: '0px 8px 32px rgba(0,0,0,0.12)', padding: '40px 40px 40px', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
            </div>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: '#4D8BF1', margin: '0 0 16px' }}>Assessment Ended</h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#374151', margin: '0 0 20px', lineHeight: '24px' }}>You switched tabs <strong>2 times</strong>.<br />Your test has been submitted automatically.</p>
            <div style={{ background: '#EEF4FF', border: '1px solid #DBEAFE', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, textAlign: 'left' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4D8BF1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1E40AF' }}>If this seems incorrect, please contact your administrator.</span>
            </div>
            <button onClick={() => { localStorage.setItem(`examDisabled_${EXAM_ID}`, 'true'); navigate('/dashboard'); }} style={{ background: '#4D8BF1', color: '#fff', border: 'none', borderRadius: 52, padding: '13px 40px', fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Return to Home</button>
          </div>
        </div>
      )}
 
      {/* ── Top Nav ── */}
      <div style={s.topNav}>
        <div style={s.topInner}>
          <img src={logo} alt="Inteliment" style={s.brandLogo} />
          <span style={s.examTitle}>{EXAM_TITLE}</span>
          <div style={s.timerPill}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7.5 12 12 15.5 13.8"/></svg>
            <span style={{ ...s.timerText, color: timeLeft < 300 ? '#FFF3BF' : '#FFFFFF' }}>{mins}:{secs}</span>
          </div>
        </div>
      </div>
 
      <div style={s.body}>
        <div style={{ ...s.cardWrap, marginRight: navOpen ? NAV_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.qLabel}>Question {current + 1}</span>
              <div style={s.progressSection}>
                <img src={progressRunnerIcon} alt="" style={{ width: 24, height: 24, flexShrink: 0 }} />
                <div style={s.progressBg}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
                <span style={s.qCount}>{current + 1} of {totalQuestionsCount}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#1B6ECF" style={{ flexShrink: 0 }}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15" stroke="#1B6ECF" strokeWidth="1.667"/></svg>
              </div>
            </div>
            <div style={s.cardDivider} />
            <div style={s.qTextWrap}><span style={s.qText}>{q.text}</span><span style={s.markBadge}>({q.marks || 1} Mark)</span></div>
            <div style={s.optionsWrap}>
              {q.options.map((opt, idx) => {
                const selected = answers[q.id] === idx;
                return (
                  <div key={idx} onClick={() => selectOption(idx)} style={{ ...s.option, backgroundColor: selected ? '#EEF4FF' : '#FAFAFA', border: selected ? '0.893px solid #4D8BF1' : '0.893px solid #AEB3BD', boxShadow: selected ? '0px 1.785px 10.712px rgba(45,107,228,0.12)' : 'none' }}>
                    <div style={{ ...s.optLetter, backgroundColor: selected ? '#4D8BF1' : '#F3F4F6', border: selected ? 'none' : '0.893px solid #E5E7EB' }}>
                      <span style={{ ...s.optLetterText, color: selected ? '#fff' : '#6B7280' }}>{['A','B','C','D'][idx]}</span>
                    </div>
                    <div style={{ ...s.radio, border: selected ? '1.785px solid #4D8BF1' : '0.893px solid #D1D5DB', backgroundColor: selected ? '#EEF4FF' : '#fff' }}>{selected && <div style={s.radioDot} />}</div>
                    <span style={{ ...s.optText, color: selected ? '#4D8BF1' : '#26282B', fontWeight: selected ? 500 : 400 }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            <div style={s.cardFooter}>
              <div style={s.footerRight}>
                <button onClick={handleSkip} style={s.skipBtn}>Skip</button>
                <button onClick={handlePrev} disabled={current === 0} style={{ ...s.navCircle, backgroundColor: current === 0 ? '#DAE0E7' : '#4D8BF1', cursor: current === 0 ? 'not-allowed' : 'pointer' }}>
                  <img src={questionNavArrow} alt="" style={{ ...s.navArrowIcon, transform: 'rotate(180deg)', opacity: current === 0 ? 0.55 : 1 }} />
                </button>
                <button
                  onClick={handleNext}
                  disabled={current === questions.length - 1}
                  style={{
                    ...s.navCircle,
                    backgroundColor: current === questions.length - 1 ? '#DAE0E7' : '#4D8BF1',
                    cursor: current === questions.length - 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <img src={questionNavArrow} alt="" style={{ ...s.navArrowIcon, opacity: current === questions.length - 1 ? 0.55 : 1 }} />
                </button>
              </div>
            </div>
          </div>
        </div>
 
        {/* Collapsed strip */}
        {!navOpen && (
          <div style={s.collapsedStrip}>
            <button onClick={() => setNavOpen(true)} style={s.stripArrowBtn}><svg width="7" height="14" viewBox="0 0 7 14" fill="none"><polyline points="5,2 2,7 5,12" stroke="#316EE5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg></button>
            <button onClick={() => setShowHelp(p => !p)} style={s.helpBtn}><svg width="28" height="28" viewBox="0 0 64 64" fill="none"><ellipse cx="38" cy="36" rx="18" ry="16" fill="#A0A8C0" opacity="0.7"/><circle cx="28" cy="28" r="20" fill="#4D8BF1"/><text x="28" y="36" textAnchor="middle" fontSize="22" fontWeight="bold" fill="white" fontFamily="Arial">?</text></svg></button>
          </div>
        )}
 
        {showHelp && !navOpen && (
          <div style={s.helpTooltip}>
            <p style={s.helpTooltipTitle}>Need help?</p>
            <p style={s.helpTooltipText}>Use navigation arrows to move between questions.</p>
            <button onClick={() => setShowHelp(false)} style={s.helpTooltipClose}>Got it</button>
          </div>
        )}
 
        {/* ─────────────────────────────────────────────────────
            UPDATED QUESTION NAVIGATOR PANEL — matches Image 2
        ───────────────────────────────────────────────────── */}
        {navOpen && (
          <div style={s.navPanel}>
 
            {/* Close button */}
            <button onClick={() => setNavOpen(false)} style={s.closeBtn}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <line x1="1" y1="1" x2="11" y2="11" stroke="#6B7280" strokeWidth="1.8"/>
                <line x1="11" y1="1" x2="1" y2="11" stroke="#6B7280" strokeWidth="1.8"/>
              </svg>
            </button>

            <div style={s.navContent}>
            <div style={s.legendBlock}>
              <span style={s.navTitle}>Question Navigator</span>
              <div style={s.legendGrid}>
                <div style={s.legendItem}>
                  <div style={{ ...s.legendDot, background: '#fff', border: '0.95px solid #4D8BF1' }} />
                  <span style={s.legendLabel}>Total</span>
                  <span style={s.legendCount}>{String(totalQuestionsCount).padStart(2, '0')}</span>
                </div>
                <div style={s.legendItem}>
                  <div style={{ ...s.legendDot, background: '#4D8BF1' }} />
                  <span style={s.legendLabel}>Answered</span>
                  <span style={s.legendCount}>{String(answered).padStart(2, '0')}</span>
                </div>
                <div style={s.legendItem}>
                  <div style={{ ...s.legendDot, background: '#F5A623' }} />
                  <span style={s.legendLabel}>Skipped Questions</span>
                  <span style={s.legendCount}>{String(skippedCount).padStart(2, '0')}</span>
                </div>
                <div style={s.legendItem}>
                  <div style={{ ...s.legendDot, background: '#E0E6F0', border: '0.95px solid #C8D0E0' }} />
                  <span style={s.legendLabel}>Not Visited</span>
                  <span style={s.legendCount}>{String(notVisited).padStart(2, '0')}</span>
                </div>
              </div>
            </div>

            <div style={s.navDivider} />
 
            {/* ── SELECT SECTIONS ── */}
            <div style={s.navSectionHeader}>
              <span style={s.navSectionTitle}>Select Sections</span>
            </div>
 
            {/* ── Section pills ── */}
            <div style={s.pillsWrap}>
              {sectionCounts.map((sec) => {
                const isActive = activeSection === sec.sectionIndex;
                return (
                  <button
                    key={sec.name}
                    onClick={() => switchSection(sec.sectionIndex)}
                    style={{
                      display: 'flex', flexDirection: 'row', alignItems: 'center',
                      padding: '9px 13px', gap: 9, borderRadius: 10,
                      border: `1px solid ${isActive ? '#4D8BF1' : '#6B7280'}`,
                      background: '#fff', cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 14,
                      color: isActive ? '#4D8BF1' : '#6B7280',
                      transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}
                  >
                    <span>{sec.name}</span>
                    <span style={{ fontSize: 12, opacity: 0.85 }}>{sec.count} Qs</span>
                  </button>
                );
              })}
            </div>
 
            {/* ── Divider ── */}
            <div style={{ display: 'none' }} />
 
            {/* ── Questions header + pagination ── */}
            <div style={s.qNavRow}>
              <span style={s.navQLabel}>Questions</span>
              <div style={s.paginationWrap}>
                <button onClick={() => setNavPage(p => Math.max(1, p - 1))} style={{ ...s.pageCtrl, opacity: navPage === 1 ? 0.4 : 1 }}>
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M8 3L5 6.5L8 10" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                {Array.from({ length: totalNavPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setNavPage(p)} style={{ ...s.pageNum, background: navPage === p ? '#4D8BF1' : '#fff', border: navPage === p ? '1px solid #4D8BF1' : '1px solid #F1F1F1', color: navPage === p ? '#fff' : '#333' }}>{p}</button>
                ))}
                <button onClick={() => setNavPage(p => Math.min(totalNavPages, p + 1))} style={{ ...s.pageCtrl, opacity: navPage === totalNavPages ? 0.4 : 1 }}>
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M5 3L8 6.5L5 10" stroke="#333" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            </div>
 
            {/* ── Question number grid ── */}
            <div style={s.gridWrap}>
              <div style={s.grid}>
                {pagedQuestions.map((qItem) => {
                  const status    = getStatus(qItem.id);
                  const globalIdx = questions.findIndex(qq => qq.id === qItem.id);
                  return (
                    <button
                      key={qItem.id}
                      onClick={() => setCurrent(globalIdx)}
                      style={{
                        ...s.gridBtn,
                        backgroundColor: status === 'current'  ? '#FFFFFF'
                                        : status === 'answered' ? '#4D8BF1'
                                        : status === 'skipped'  ? '#F5A623'
                                        : '#FCFDFD',
                        border: status === 'current'  ? '1.9px solid #4D8BF1'
                              : status === 'answered' ? '1px solid #4D8BF1'
                              : status === 'skipped'  ? '1px solid #F5A623'
                              : '0.95px solid #E0E6F0',
                        color: status === 'current'  ? '#2D6BE4'
                             : status === 'answered' ? '#FFFFFF'
                             : status === 'skipped'  ? '#FFFFFF'
                             : '#666666',
                        boxShadow: status === 'current' || status === 'answered'
                          ? '0px 1.9px 7.58px rgba(45,107,228,0.15)' : 'none',
                      }}
                    >
                      {globalIdx + 1}
                    </button>
                  );
                })}
              </div>
            </div>
            </div>
 
            {/* ── Divider ── */}
            <div style={{ display: 'none' }} />
 
            {/* ── Question Navigator legend ── */}
 
            {/* ── Submit button ── */}
            <div style={s.submitWrap}>
              <button
                onClick={() => setShowSubmitModal(true)}
                disabled={submitting}
                style={{ ...s.submitBtn, opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                onMouseEnter={e => { if (!submitting) { e.currentTarget.style.backgroundColor = '#4D8BF1'; e.currentTarget.style.color = '#FFFFFF'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(77,139,241,0.22), 0 4px 20px rgba(77,139,241,0.4)'; } }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.style.color = '#26282B'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        )}
      </div>
 
      {/* ── Submit confirmation modal ── */}
      {showSubmitModal && (
        <div style={s.overlay}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 28, width: 448, height: 367, maxWidth: '100%', textAlign: 'center', boxShadow: '0 14px 42px rgba(30, 64, 175, 0.22)', border: '1px solid rgba(77, 139, 241, 0.12)', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box' }}>
            <button onClick={() => setShowSubmitModal(false)} style={{ position: 'absolute', top: 16, right: 16, width: 40, height: 40, borderRadius: '50%', border: 'none', background: '#F3F4F6', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><line x1="2" y1="2" x2="10" y2="10" stroke="#4B5563" strokeWidth="1.7" strokeLinecap="round"/><line x1="10" y1="2" x2="2" y2="10" stroke="#4B5563" strokeWidth="1.7" strokeLinecap="round"/></svg>
            </button>
            <div style={{ width: '100%', padding: '16px 32px 0', boxSizing: 'border-box' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FFF3E8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M12 8.25V12.75" stroke="#F28C28" strokeWidth="1.8" strokeLinecap="round"/><path d="M12 16.25H12.01" stroke="#F28C28" strokeWidth="2.2" strokeLinecap="round"/><path d="M10.616 3.966L2.39 18.139C1.623 19.46 2.576 21.125 4.104 21.125H19.896C21.424 21.125 22.377 19.46 21.61 18.139L13.384 3.966C12.62 2.65 11.38 2.65 10.616 3.966Z" stroke="#F28C28" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <h2 style={{ width: 384, maxWidth: '100%', minHeight: 32, fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 32, lineHeight: '32px', letterSpacing: '-0.02em', color: '#0F172A', margin: '0 auto 26px' }}>Submit Assessment?</h2>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, lineHeight: '24px', color: '#2F2F2F', margin: '40px 0 10px' }}>ARE YOU SURE YOU WANT TO SUBMIT THIS EXAM?</p>
              <p style={{ width: 342, maxWidth: '100%', height: 46, fontFamily: "'Inter', sans-serif", fontSize: 13, lineHeight: '23px', color: '#667085', margin: '0 auto' }}>Once the exam is submitted, you cannot go back to the assessment.</p>
            </div>
            <div style={{ marginTop: 'auto', paddingBottom: 28 }}>
              <button
                onClick={() => { setShowSubmitModal(false); handleSubmit('manual'); }}
                style={{ width: 240, height: 48, borderRadius: 999, border: 'none', background: 'linear-gradient(180deg, #5D94F0 0%, #4D82DE 100%)', boxShadow: '0 8px 18px rgba(77, 139, 241, 0.18)', fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, lineHeight: '18px', color: '#fff', cursor: 'pointer', padding: 0 }}
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 
const s = {
  page         : { width: '100%', minHeight: '111.12vh', backgroundColor: '#C4DAFD', fontFamily: "'Inter', sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  overlay      : { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.38)', backdropFilter: 'blur(2px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  topNav       : { width: '100%', backgroundColor: '#FFFFFF', boxShadow: '0px 2px 24px rgba(20,63,147,0.16)', borderRadius: '0px 0px 40px 40px', zIndex: 300, height: 125, display: 'flex', alignItems: 'center', padding: '0 24px', boxSizing: 'border-box' },
  topInner     : { width: '100%', maxWidth: 1244, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' },
  brandLogo    : { height: 58, width: 'auto', objectFit: 'contain' },
  examTitle    : { fontSize: 20, color: '#374151', position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' },
  timerPill    : { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'linear-gradient(180deg, #2D6BE4 0%, #5B8DEF 100%)', borderRadius: 999, width: 122, height: 44 },
  timerIcon    : {},
  timerText    : { fontSize: 16, fontWeight: 700, color: '#fff' },
  body         : { flex: 1, display: 'flex', position: 'relative', overflow: 'hidden', paddingTop: 0 },
  cardWrap     : { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 48px', minWidth: 0 },
  card         : { width: 803, height: 519, backgroundColor: '#FFFFFF', borderRadius: 40, boxShadow: '0px 4px 6px rgba(0,0,0,0.13)', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', padding: '1.947px' },
  cardHeader   : { display: 'flex', justifyContent: 'space-between', padding: '14px 28.566px 0', minHeight: 55 },
  qLabel       : { fontWeight: 600, fontSize: 12.5, color: '#4D8BF1' },
  progressSection: { display: 'flex', alignItems: 'center', gap: 8 },
  progressBg   : { width: 220, height: 8, backgroundColor: '#E5E7EB', borderRadius: 999, overflow: 'hidden' },
  progressFill : { height: '100%', background: 'linear-gradient(90deg, #4D8BF1 0%, #6B9FF7 100%)', transition: 'width 0.3s ease' },
  qCount       : { fontSize: 10.7, color: '#26282B', marginTop: 2 },
  cardDivider  : { height: '0.893px', backgroundColor: '#F3F4F6', margin: '0 28.566px' },
  qTextWrap    : { padding: '24px 30.95px 0', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) max-content', alignItems: 'start', gap: 12 },
  qText        : { fontSize: 16, color: '#0D1117', flex: 1, lineHeight: '25px' },
  markBadge    : { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'max-content', minWidth: 0, backgroundColor: '#F9FAFB', border: '0.893px solid #C5C5C5', borderRadius: 89.27, padding: '3px 8px', fontSize: 9.8, lineHeight: '13px', color: '#26282B', whiteSpace: 'nowrap', boxSizing: 'border-box' },
  optionsWrap  : { display: 'flex', flexDirection: 'column', gap: 8.9, padding: '27px 29px 0 28.566px' },
  option       : { display: 'flex', alignItems: 'flex-start', padding: '13px 16px 13px 18.747px', gap: 14.2, minHeight: 53, borderRadius: 12.5, transition: 'all 0.15s ease', cursor: 'pointer' },
  optLetter    : { width: 26.7, minWidth: 26.7, maxWidth: 26.7, height: 26.7, minHeight: 26.7, maxHeight: 26.7, borderRadius: 8.9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box' },
  optLetterText: { fontWeight: 700, fontSize: 10.7 },
  radio        : { width: 19.6, minWidth: 19.6, maxWidth: 19.6, height: 19.6, minHeight: 19.6, maxHeight: 19.6, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box', marginTop: 3 },
  radioDot     : { width: 8.9, height: 8.9, borderRadius: '50%', backgroundColor: '#4D8BF1' },
  optText      : { fontSize: 14.2, color: '#26282B', flex: 1, lineHeight: 1.35, wordBreak: 'break-word' },
  cardFooter   : { marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', padding: '0 44px 28px 0' },
  footerRight  : { display: 'flex', alignItems: 'center', gap: 18 },
  skipBtn      : { background: 'none', border: 'none', fontSize: 16, fontWeight: 500, color: '#6B7280', cursor: 'pointer' },
  navCircle    : { width: 44, height: 44, borderRadius: '50%', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navArrowIcon : { width: 20 },
 
  // Collapsed strip
  collapsedStrip  : { position: 'fixed', right: 0, top: 190, width: 48, height: 480, backgroundColor: '#FFFFFF', borderRadius: '20px 0px 0px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', boxShadow: '0px 2px 24px rgba(20,63,147,0.16)', zIndex: 100 },
  stripArrowBtn   : { border: 'none', background: 'none', cursor: 'pointer' },
  helpBtn         : { border: 'none', background: 'none', cursor: 'pointer' },
  helpTooltip     : { position: 'fixed', right: 56, bottom: 120, width: 240, backgroundColor: '#FFFFFF', borderRadius: 12, padding: '16px', boxShadow: '0px 8px 24px rgba(20,63,147,0.18)', zIndex: 200 },
  helpTooltipTitle: { fontWeight: 600, fontSize: 14, margin: 0 },
  helpTooltipText : { fontSize: 12.5, color: '#555', margin: '8px 0' },
  helpTooltipClose: { alignSelf: 'flex-end', background: '#4D8BF1', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: 13, cursor: 'pointer' },
 
  // ── Updated nav panel ──
  navPanel         : { position: 'fixed', right: 0, top: 0, bottom: 0, width: 455, height: 'auto', backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', borderBottomLeftRadius: 0, zIndex: 200, overflowY: 'auto', paddingTop: 152, boxSizing: 'border-box' },
  navContent       : { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box', paddingTop: 34, paddingBottom: 18 },
  closeBtn         : { position: 'absolute', top: 174, right: 20, border: '1px solid #E5E7EB', background: '#F9FAFB', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 },
 
  // Select Sections
  navSectionHeader : { order: 2, width: 408, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '0 0 12px', boxSizing: 'border-box' },
  navSectionTitle  : { fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: '#111827' },
  pillsWrap        : { order: 2, width: 408, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 12, padding: '0 0 20px', boxSizing: 'border-box' },
 
  // Questions row + pagination
  qNavRow       : { order: 3, width: 408, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 16px', boxSizing: 'border-box' },
  navQLabel     : { fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: '#111827' },
  paginationWrap: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 7 },
  pageCtrl      : { width: 32, height: 32, background: '#fff', border: '1px solid #EEF2F7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 },
  pageNum       : { width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Open Sans', sans-serif", fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0, transition: 'all 0.15s' },
 
  // Grid
  gridWrap : { order: 3, width: 408, padding: '0 0 22px', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' },
  grid     : { display: 'grid', gridTemplateColumns: 'repeat(5, 50px)', columnGap: '23px', rowGap: '16px' },
  gridBtn  : { width: 50, height: 42, borderRadius: 8, fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s ease' },
 
  // Legend
  legendBlock : { width: 408, padding: '0 0 10px', order: 0, boxSizing: 'border-box' },
  navDivider  : { width: 408, height: 1, background: '#E0E6F0', margin: '0 0 18px', order: 1 },
  navTitle    : { fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: '#111827', display: 'block', marginBottom: 14 },
  legendGrid  : { display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 12, columnGap: 42, marginBottom: 16 },
  legendItem  : { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendDot   : { width: 15, height: 15, borderRadius: 4, flexShrink: 0 },
  legendLabel : { fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#555', flex: 1 },
  legendCount : { fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 700, color: '#1A1A2E' },
 
  // Submit
  submitWrap : { order: 4, width: '100%', padding: '22px 22px 28px', marginTop: 'auto', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' },
  submitBtn  : { width: 190, height: 52, border: '1px solid #4D8BF1', borderRadius: 999, backgroundColor: '#ffffff', fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 17, color: '#26282B', letterSpacing: 0.1, transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease' },
};
